/**
 * Investor Distribution Service
 *
 * Handles investor-specific distribution operations for the portal.
 */

import { investorPortalApiClient } from '../../shared/utils/api-client';

interface RawInvestorDistributionSummary {
  id: string;
  fund_id: string;
  fund_name: string;
  distribution_number: number;
  total_amount: string;
  investor_amount: string;
  currency: string;
  distribution_date: string;
}

interface RawInvestorDistributionDetail extends RawInvestorDistributionSummary {
  description?: string;
}

export interface InvestorDistributionSummary {
  id: string;
  fund_id: string;
  fund_name: string;
  distribution_number: number;
  total_amount: number;
  investor_amount: number;
  currency: string;
  distribution_date: string;
}

export interface InvestorDistributionDetail extends InvestorDistributionSummary {
  description?: string;
}

export interface InvestorDistributionsList {
  distributions: InvestorDistributionSummary[];
  hidden_fund_count: number;
}

function mapSummary(
  raw: RawInvestorDistributionSummary
): InvestorDistributionSummary {
  return {
    id: raw.id,
    fund_id: raw.fund_id,
    fund_name: raw.fund_name,
    distribution_number: raw.distribution_number,
    total_amount: Number(raw.total_amount) || 0,
    investor_amount: Number(raw.investor_amount) || 0,
    currency: raw.currency,
    distribution_date: raw.distribution_date,
  };
}

function mapDetail(
  raw: RawInvestorDistributionDetail
): InvestorDistributionDetail {
  return {
    ...mapSummary(raw),
    description: raw.description,
  };
}

export class InvestorDistributionService {
  /**
   * Get all distributions for the current investor
   */
  static async getInvestorDistributions(): Promise<InvestorDistributionsList> {
    const response = await investorPortalApiClient.get<{
      distributions: RawInvestorDistributionSummary[];
      total: number;
      hidden_fund_count: number;
    }>('/portal/transactions/distributions');

    return {
      distributions: response.distributions.map(mapSummary),
      hidden_fund_count: response.hidden_fund_count,
    };
  }

  static async getInvestorDistributionDetail(
    distributionId: string
  ): Promise<InvestorDistributionDetail> {
    const raw =
      await investorPortalApiClient.get<RawInvestorDistributionDetail>(
        `/portal/transactions/distributions/${distributionId}`
      );
    return mapDetail(raw);
  }
}
